import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import {
  getChangedContentSettingFields,
  updateTenantContentSettings,
} from "@/lib/utils/content-settings/tenant-content-settings-api";
import type { ContentSettings } from "@/lib/utils/content-settings/content-settings-defaults";

export const useTenantContentSettingsUpdate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (request: {
      tenantId: number;
      settings: ContentSettings;
      baseline: ContentSettings;
      persistedKeys: ReadonlySet<keyof ContentSettings>;
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const fieldsToUpdate = getChangedContentSettingFields(
        request.baseline,
        request.settings,
      );

      if (fieldsToUpdate.length === 0) {
        return request.settings;
      }

      return updateTenantContentSettings(
        request.tenantId,
        request.settings,
        accessToken,
        {
          fieldsToUpdate,
          persistedKeys: request.persistedKeys,
        },
      );
    },
    retry: 1,
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.TenantContentSettings, variables.tenantId],
      });

      toast({
        title: "Content settings updated",
        description: "Content settings updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update content settings";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
